import React from 'react';
import { pathOr, isNil, isEmpty } from 'ramda';
import { AppContext } from '../../../containers/App/context';
import { Field } from 'redux-form';
import {
  renderCheckboxFieldTwoColumns,
  renderSelectFieldTwoColumns,
} from '../../../utils/redux-form-helper';
import { FormattedMessage } from 'react-intl';
import messages from '../messages';
import CamRecordService from '../../../services/camRecordService';

const defaultOption = { value: 'default', text: 'Choose' };
const generateOptions = (data, shape) => {
  return data.map((datum) => {
    return {
      value: pathOr('', shape.value, datum),
      text: pathOr('', shape.text, datum),
      disabled: pathOr(false, ['disabled'], datum),
    };
  });
};
/*
  TODO refactor with
    fortress-frontend/src/components/ViewSingleTransactionModal/ModifyTransactionCamPoolModal/form.js
*/
export default function CamPoolInput(props = {}) {
  const {
    householdId,
    formValues,
    subjournals,
    transactionTypes,
    transactionCodes,
    onChange,
    fieldsProps,
    camAllocationsId, // use to set default cam data
  } = props;

  const appContext = React.useContext(AppContext);

  const selectedSubjournal = subjournals.find(
    (s) => s.id === formValues.subjournalId,
  );
  const selectedTransactionType = transactionTypes.find(
    (t) => t.id === formValues.transactionTypeId,
  );
  const selectedSubjournalName =
    selectedSubjournal?.masterSubjournalType?.description ?? '';
  const selectedTransactionTypeName = selectedTransactionType?.name ?? '';

  const selectedSubjournalIsApplicableToCam = React.useMemo(
    () => ['cam', 'resident'].includes(selectedSubjournalName.toLowerCase()),
    [selectedSubjournalName],
  );

  const selectedTransactionTypeIsApplicableToCam = React.useMemo(
    () =>
      ['charge', 'credit'].includes(selectedTransactionTypeName.toLowerCase()),
    [selectedTransactionTypeName],
  );

  const isPayment = React.useMemo(() => {
    const selectedCode = transactionCodes.find((code) => {
      return code.id === formValues.transactionCodeId;
    });
    return !!selectedCode
      ? pathOr(false, ['transactionCode', 'isPayment'], selectedCode)
      : false;
  }, [formValues.transactionCodeId, transactionCodes]);

  const [camPools, setCamPools] = React.useState([]);
  React.useEffect(() => {
    if (props.camPools) {
      setCamPools(props.camPools);
      return;
    }
    if (
      camPools?.length > 0 ||
      !householdId ||
      !selectedSubjournalIsApplicableToCam ||
      !selectedTransactionTypeIsApplicableToCam ||
      isPayment
    ) {
      return;
    }

    const camRecordService = new CamRecordService();
    camRecordService
      .getCamPools(
        appContext.selectedProperty.organizationId,
        appContext.selectedProperty.id,
        householdId,
        false,
      )
      .then((response) => {
        setCamPools(response);
      });
  }, [
    householdId,
    props.camPools,
    props.householdId,
    camPools,
    selectedSubjournalIsApplicableToCam,
    selectedTransactionTypeIsApplicableToCam,
    isPayment,
    appContext.selectedProperty.organizationId,
    appContext.selectedProperty.id,
  ]);

  React.useEffect(() => {
    if (camAllocationsId && camPools?.length > 0) {
      const camRecord = camPools.find((cr) => {
        const camPools = cr.camPools.find((cp) => {
          return cp.camAllocationsId === camAllocationsId;
        });
        return camPools;
      });
      if (camRecord) {
        onChange?.('camRecord', camRecord.camRecordsId);
        onChange?.('camPool', camRecord.camPools[0].camAllocationsId);
      }
    }
  }, [camAllocationsId, camPools, onChange]);

  const showCamPools =
    appContext.selectedProperty.isCAMActive &&
    selectedSubjournalIsApplicableToCam &&
    selectedTransactionTypeIsApplicableToCam &&
    !isPayment &&
    !isNil(camPools) &&
    !isEmpty(camPools);
  const camPeriodsOptions = React.useMemo(() => {
    if (!showCamPools) {
      return [];
    }
    const periodsOptions = [
      ...generateOptions(camPools, {
        value: ['camRecordsId'],
        text: ['startDate'],
      }),
    ];
    if (periodsOptions.length !== 1) {
      periodsOptions.unshift(defaultOption);
    } else {
      onChange?.('camRecord', periodsOptions[0].value);
    }
    return periodsOptions;
  }, [showCamPools, camPools]);

  const camPoolsOptions = React.useMemo(() => {
    if (
      !showCamPools ||
      !formValues.camRecord ||
      formValues.camRecord === 'default'
    ) {
      return [defaultOption];
    }
    const selectedCam = camPools.find(
      (p) => p.camRecordsId === formValues.camRecord,
    );
    const pools = pathOr(null, ['camPools'], selectedCam);
    if (!pools) {
      return [defaultOption];
    }
    const poolsOptions = [
      ...generateOptions(pools, {
        value: ['camAllocationsId'],
        text: ['camPoolName'],
      }),
    ];
    if (poolsOptions.length !== 1) {
      poolsOptions.unshift(defaultOption);
    } else {
      onChange?.('camPool', poolsOptions[0].value);
    }
    return poolsOptions;
  }, [showCamPools, formValues.camRecord, camPools]);

  const onAnyFieldChange = React.useCallback(
    (evt: Object) => {
      const fieldName = evt.target.name;
      const fieldValue = evt.target.value;
      onChange?.(fieldName, fieldValue);
    },
    [onChange],
  );

  return (
    <>
      {showCamPools && (
        <React.Fragment>
          <Field
            name="applyToCamPool"
            col1={[4, 12]}
            col2={[8, 12]}
            component={renderCheckboxFieldTwoColumns}
            label={<FormattedMessage {...messages.applyToCamPool} />}
            onChange={onAnyFieldChange}
            {...fieldsProps}
          />
          {formValues.applyToCamPool && (
            <React.Fragment>
              <Field
                name="camRecord"
                component={renderSelectFieldTwoColumns}
                col1={[4, 12]}
                col2={[8, 12]}
                label={<FormattedMessage {...messages.camPeriod} />}
                options={camPeriodsOptions}
                onChange={onAnyFieldChange}
                {...fieldsProps}
              />
              <Field
                name="camPool"
                component={renderSelectFieldTwoColumns}
                col1={[4, 12]}
                col2={[8, 12]}
                label={<FormattedMessage {...messages.camPool} />}
                options={camPoolsOptions}
                onChange={onAnyFieldChange}
                {...fieldsProps}
              />
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </>
  );
}
